import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { DATE_TIME_FORMAT_12_UPPERCASE_TIMEZONE } from '../../../constants';
import { actions } from '../../ShipperCaps/redux/actions';
import { shippingActions } from '../../Shipping/actions';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { CHANGE_DEVICE_STATUS_HISTORY } from '../../ShipperCaps/redux/constants';
import { openModalAction } from '../../../actions/modal';
import { StateTypes } from '../../../modals/ChangeStateModal';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition } from '../../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';

function DeviceStatusHistory(props) {
  const { getDeviceTimeline, changeDeviceStatus, filters } = props;
  const [timeline, setTimeline] = React.useState([]);
  const deviceId = props.match.params.deviceId || props.match.params.id;

  React.useEffect(() => {
    getData();
  }, [deviceId]);

  const getData = () => {
    getDeviceTimeline(deviceId).then(resp => {
      if (resp?.response) {
        setTimeline(resp.response?.data || []);
      }
    });
  };

  const onStatusChange = () => {
    const data = {
      id: deviceId,
      propertyForApi: 'status',
      action: actions.changeDeviceStatus,
      actionType: CHANGE_DEVICE_STATUS_HISTORY,
      title: Strings.changeDeviceState,
      state: StateTypes.device.id,
      onSuccess: getData,
    };
    changeDeviceStatus(data);
  };

  const dataForTable = React.useMemo(() => {
    if (!timeline?.length) {
      return [];
    }

    let ret = [...timeline];

    const appliedFilters = getRelevantFilters(filters);
    const filtersToCheck = ['timelineEventType'];
    const hasFilters = filtersToCheck.some(f => appliedFilters?.some(af => af.group === f));
    
    if (hasFilters) {
      filtersToCheck.forEach(filterGroup => {
        const property = getFiltersDefinition()[filterGroup]?.propertyForApi || filterGroup;
        const filteredByGroup = appliedFilters?.filter(f => f.group === filterGroup);
        if (filteredByGroup?.length) {
          ret = ret.filter(p => filteredByGroup.some(f => f.value === p[property]));
        }
      });
    }
    
    return ret;
  }, [timeline, filters]);

  const getValueByType = (item, type) => {
    switch (item.event_type) {
      case 'status_changed':
      case 'kit_added':
      case 'kit_removed':  
        return item[type];
      case 'shipment_new':
      case 'shipment_created':
      case 'shipment_in_transit':
      case 'shipment_delivered':
      case 'shipment_cancelled':
        if (type === 'new_value') {
          return item.new_value || '-';
        }
        return '-';
      case 'org_assigned':
        if (type === 'new_value') {
          return item.org_name;
        }
        return '-';
      case 'org_unassigned':
        if (type === 'old_value') {
          return item.org_name;
        }
        return '-';
      default:
        return '-';
    }
  };

  const columns = React.useMemo(() => [
    columnHelper.accessor('event_at', {
      header: Strings.date,
      cell: info => (info.getValue() ? moment(info.getValue()).format(DATE_TIME_FORMAT_12_UPPERCASE_TIMEZONE) : '-'),
      size: 120,
    }),
    columnHelper.accessor('actor_name', {
      header: Strings.changedBy,
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('old_value', {
      header: Strings.oldValue,
      cell: info => getValueByType(info.row.original, 'old_value') || '-',
      size: 120,
    }),
    columnHelper.accessor('new_value', {
      header: Strings.newValue,
      cell: info => getValueByType(info.row.original, 'new_value') || '-',
      size: 120,
    }),
    columnHelper.accessor('org_name', {
      header: Strings.organization,
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('note', {
      header: Strings.note,
      cell: info => info.getValue() || '-',
      size: 200,
    }),
    columnHelper.accessor('event_type', {
      header: Strings.eventType,
      cell: info => info.getValue() ? Strings.timelineEventTypesEnum[info.getValue()] || '-' : '-',
      size: 120,
    }),
  ], []);

  return (
    <>
      <PageHeader
        left={Strings.deviceStatusHistory}
        right={(
          <>
            <AdvancedFilters>
              <Filter definition={getFiltersDefinition().timelineEventType} />
            </AdvancedFilters>
            <Button onClick={onStatusChange} class={HEADER_BUTTON_DARK_BLUE} title={Strings.changeDeviceState} />
          </>
        )}
      />
      <AdvancedFiltersBar />
      <Table3
        name="device-status-history"
        data={dataForTable}
        columns={columns}
      />
    </>
  );
}

DeviceStatusHistory.propTypes = {
  activeTab: PropTypes.string,
  getDeviceTimeline: PropTypes.func.isRequired,
  changeDeviceStatus: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getDeviceTimeline: deviceId => dispatch(shippingActions.getDeviceTimeline(deviceId)),
  changeDeviceStatus: data => dispatch(openModalAction('change-state', data)),
});

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});


export default connect(mapStateToProps, mapDispatchToProps)(DeviceStatusHistory);
